import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useGQL_placeById, useGQL_teams, postDeletePlace } from '@/contexts/graphql-and-swr-apis'
import { postUpdatePlace } from '@/contexts/graphql-functions'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { flushSync } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faDownload, faQrcode, faXmark, faKeySkeleton, faTrash, faFont, faPen, faUnlock } from '@fortawesome/pro-solid-svg-icons'
import { faEye } from '@fortawesome/pro-regular-svg-icons'
import QRCode from 'react-qr-code'
import CardDetailItem from '@/components/cards/CardDetailItem'
import { SimpleCard } from '@unwrittenmedia/simple-components'
import { formatTimestampToLocalClockTime, formatTimeAgo } from '@/helpers/timestuff'
import { uploadSlideToLiveApi } from '@/helpers/uploadSlide'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'
import { useWSPlaceUpdates, type SlideInfo } from '@/hooks/useWSPlaceUpdates'
import { useNow } from '@/hooks/useNow'
import { FunDropdownMenu, type FunMenuItem } from '@unwrittenmedia/simple-components'
import SimpleModalDialog from '@/components/SimpleModalDialog'
import { EncoderKeyDialogBody } from '@/components/EncoderKeyDialogBody'
import { EncoderStatusDebugger } from '@/components/debug/EncoderStatusDebugger'
import { useScreenStatus } from '@/hooks/useScreenStatus'
import { UrlQRCodeDialogBody, useDownloadQRCode } from '@/components/popups/UrlQRPopupImage'
import { createAuthGuard } from '@/lib/authGuard'

export const Route = createFileRoute('/team/$teamId/place/$placeId')({
  beforeLoad: createAuthGuard(),
  component: PlaceDetailPage,
})

const doAddTextGeneratedSlide = async (placeId: string) => {
  const userText = prompt('Enter text for the slide:')
  if (!userText || userText.trim() === '') {
    console.log('Text slide creation cancelled')
    return
  }

  const canvas = document.createElement('canvas')
  canvas.width = 1920
  canvas.height = 1080
  canvas.style.display = 'none'
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Failed to get canvas context')
    document.body.removeChild(canvas)
    return
  }

  const hue = Math.floor(Math.random() * 360)
  ctx.fillStyle = `hsl(${hue}, 30%, 30%)`
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const maxWidth = canvas.width * 0.8
  let fontSize = 200
  ctx.font = `bold ${fontSize}px Arial`
  ctx.fillStyle = '#FFFFFF'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const centerX = canvas.width / 2
  const centerY = canvas.height / 2

  let metrics = ctx.measureText(userText)
  while (metrics.width > maxWidth && fontSize > 10) {
    fontSize -= 10
    ctx.font = `bold ${fontSize}px Arial`
    metrics = ctx.measureText(userText)
  }

  ctx.fillText(userText, centerX, centerY)

  const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.95)
  document.body.removeChild(canvas)

  try {
    await uploadSlideToLiveApi(jpegDataUrl, placeId)
    console.log('should refresh slide list')
  } catch (error) {
    console.error('Failed to upload text slide:', error)
  }
}

// Dialog body for renaming a place
function RenamePlaceDialogBody({ data }: { data: { currentName: string; value: string; onChange: (v: string) => void } }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-mist-400 mb-1">
        New name
      </label>
      <input
        type="text"
        autoFocus
        defaultValue={data.currentName}
        onChange={(e) => data.onChange(e.target.value)}
        placeholder={data.currentName}
        className="block w-full rounded-md border border-gray-300 dark:border-mist-700 bg-white dark:bg-mist-800 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  )
}


function PlaceDetailPage() {
  const { teamId, placeId } = Route.useParams();
  const navigate = useNavigate();
  const status = useScreenStatus(placeId);
  const [showEncoderKeyDialog, setShowEncoderKeyDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [showQRCodeDialog, setShowQRCodeDialog] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const handleDownloadQRCode = useDownloadQRCode(qrCodeRef, placeId);

  const pageMenuItems: FunMenuItem[] = useMemo(() => [
    { id: 'add-text-slide', label: 'Add Text Slide', icon: faFont },
    { id: 'toggle-public', label: 'Visibility', icon: faUnlock },
    { id: 'break1', label: '', isBreak: true },
    { id: 'rename', label: 'Rename Place', icon: faPen },
    { id: 'encoder-key', label: 'View Encoder Key', icon: faKeySkeleton },
    { id: 'delete', label: 'Delete Place', icon: faTrash, variant: 'danger' },
  ], [])

  const handlePageMenuAction = async (action: string) => {
    if (action === 'add-text-slide') {
      await doAddTextGeneratedSlide(placeId)
    } else if (action === 'rename') {
      setRenameValue(placeData?.place?.name ?? '')
      setShowRenameDialog(true)
    } else if (action === 'encoder-key') {
      setShowEncoderKeyDialog(true)
    } else if (action === 'toggle-public') {
      // TODO: implement toggle public/private
      console.log('Toggle public/private – not yet implemented')
    } else if (action === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to delete "${placeData?.place?.name ?? 'this place'}"? This cannot be undone.`
      )
      if (!confirmed) return
      try {
        await postDeletePlace(placeId)
        navigate({ to: '/venues' })
      } catch (err) {
        console.error('Failed to delete place:', err)
        alert('Failed to delete the place. Please try again.')
      }
    }
  }

  // Fetch place details
  const { data: placeData, error: placeError, isLoading: placeLoading } = useGQL_placeById(placeId, { encoderKey: true })

  // Fetch teams to get team name
  const { data: teamsData } = useGQL_teams()
  const team = useMemo(() => {
    return teamsData?.teams.find(t => t.id === teamId)
  }, [teamsData, teamId])

  // Retrieve slides via WebSocket — shared between the preview and the full slides card
  const { slides, isConnecting: slideLoading, connectionStatus } = useWSPlaceUpdates({
    placeId,
    enabled: true,
    since: 3600,
  })
  const slideError = connectionStatus === 'failed' ? new Error('Connection failed') : null

  // Keep time-ago labels fresh every 10 seconds
  useNow(10_000)

  const latestSlide = slides[0] ?? null

  const place = placeData?.place

  if (placeLoading) {
    return (
      <PageContentWrapper>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-gray-900 dark:text-white">Loading place details...</div>
        </div>
      </PageContentWrapper>
    )
  }

  if (placeError) {
    return (
      <PageContentWrapper>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600 dark:text-red-400">Error loading place: {placeError.message}</div>
        </div>
      </PageContentWrapper>
    )
  }

  if (!place) {
    return (
      <PageContentWrapper>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-gray-900 dark:text-white">Place not found</div>
        </div>
      </PageContentWrapper>
    )
  }

  return (
    <PageContentWrapper>
      <SimplePageHeader2
        title={place.name}
        titleBreadcrumbs={[{ label: team?.name ?? 'Team', to: `/team/${teamId}/manage` }]}
        description={`Details and slides for ${place.name}`}
        actionMenu={pageMenuItems}
        actionButtonOnClick={(action) => { if (action) handlePageMenuAction(action) }}
      />

      {/* Rename Place Modal */}
      <SimpleModalDialog
        open={showRenameDialog}
        onClose={async (buttonId) => {
          if (buttonId === 'save') {
            const trimmed = renameValue.trim()
            if (trimmed && trimmed !== place.name) {
              try {
                await postUpdatePlace(placeId, trimmed)
              } catch (err) {
                console.error('Failed to rename place:', err)
                alert('Failed to rename the place. Please try again.')
              }
            }
          }
          setShowRenameDialog(false)
        }}
        dialogTitle="Rename Place"
        icon={faPen}
        iconColor="indigo"
        BodyComponent={RenamePlaceDialogBody}
        data={{ currentName: place.name, value: renameValue, onChange: setRenameValue }}
        buttons={[
          { id: 'cancel', label: 'Cancel', color: 'secondary', size: 'md' },
          { id: 'save', label: 'Save', color: 'primary', size: 'md' },
        ]}
      />

      {/* Encoder Key Modal */}
      <SimpleModalDialog
        open={showEncoderKeyDialog}
        onClose={() => setShowEncoderKeyDialog(false)}
        dialogTitle={`${place.name}: Encoder Key`}
        icon={faKeySkeleton}
        iconColor="green"
        BodyComponent={EncoderKeyDialogBody}
        data={{ id: placeId }}
        buttons={[
          { id: 'close', label: 'Close', color: 'primary', size: 'md' },
        ]}
      />

      {/* QR Code Modal */}
      <SimpleModalDialog
        open={showQRCodeDialog}
        onClose={() => setShowQRCodeDialog(false)}
        dialogTitle="Share With Attendees"
        icon={faQrcode}
        iconColor="blue"
        BodyComponent={UrlQRCodeDialogBody}
        data={{ 
          url: `${window.location.origin}/watch/${place.place_code}`, 
          placeId,
          onDownload: () => handleDownloadQRCode(placeId),
          qrCodeRef
        }}
        buttons={[
          { id: 'close', label: 'Close', color: 'secondary', size: 'md' },
        ]}
      />

      {/* 2-column grid: Place Information + Latest Slide */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Place Information Card */}
        <div className="bg-white dark:bg-mist-900 shadow rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-mist-800">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-mist-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Place Information</h3>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <CardDetailItem title="Team">
                {team?.name || 'Unknown'}
              </CardDetailItem>

              <CardDetailItem title="Address" hide={!place.address}>
                {place.address}
              </CardDetailItem>

              <CardDetailItem title="Live Viewers" hide={status?.viewerCount === undefined}>
                {status?.viewerCount}
              </CardDetailItem>

              <CardDetailItem title="Description" hide={!place.description}>
                {place.description}
              </CardDetailItem>

              {/* <CardDetailItem title="Coordinates" hide={place.latitude === undefined || place.longitude === undefined}>
                {place.latitude !== undefined && place.longitude !== undefined && (
                  <span>
                    {place.latitude.toFixed(6)}, {place.longitude.toFixed(6)}
                  </span>
                )}
              </CardDetailItem> */}

              <CardDetailItem title="Public Share Link" hide={false} bodyClassName="font-mono">
                {`${window.location.origin}/watch/${place.place_code}`}
                <FontAwesomeIcon icon={faCopy} className="ml-2 cursor-pointer text-blue-600 dark:text-blue-400" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/watch/${place.place_code}` || '')
                }} />
              </CardDetailItem>

              <CardDetailItem title="Public Place Code" hide={!place.place_code}>
                <span className="font-mono">{place.place_code}</span>
                <FontAwesomeIcon icon={faCopy} className="ml-2 cursor-pointer text-blue-600 dark:text-blue-400" onClick={() => {
                  navigator.clipboard.writeText(place.place_code || '')
                }} />
                <FontAwesomeIcon icon={faQrcode} className="ml-2 cursor-pointer text-blue-600 dark:text-blue-400" onClick={() => {
                  setShowQRCodeDialog(true)
                }} />
              </CardDetailItem>
            </dl>
          </div>
        </div>

        {/* Latest Slide Preview */}
        <div className="bg-white dark:bg-mist-900 shadow rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-mist-800">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-mist-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Latest Slide</h3>
          </div>
          <div className="px-4 py-5 sm:px-6 flex items-center justify-center">
            {slideLoading ? (
              <p className="text-gray-500 dark:text-mist-500 text-sm">Connecting…</p>
            ) : latestSlide ? (
              <div className="w-full max-w-sm">
                <SlideCard slide={latestSlide} />
              </div>
            ) : (
              <p className="text-gray-500 dark:text-mist-500 text-sm">No slides yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Encoder Status Debugger */}
      <EncoderStatusDebugger placeId={placeId} status={status} />

      {/* Slides Grid Card */}
      <PlaceSlidesCard
        placeId={placeId}
        slides={slides}
        slideLoading={slideLoading}
        slideError={slideError}
      />
    </PageContentWrapper>
  )
}

// ─── components ─────────────────────────────────────────────────────────────

interface PlaceSlidesCardProps {
  placeId: string
  slides: SlideInfo[]
  slideLoading: boolean
  slideError: Error | null
}

function PlaceSlidesCard({ placeId, slides, slideLoading, slideError }: PlaceSlidesCardProps) {
  const [expanded, setExpanded] = useState(false)
  const gridRef = useRef<HTMLDivElement>(null)
  const [rowHeight, setRowHeight] = useState<number | null>(null)
  const [gridHeight, setGridHeight] = useState<number | null>(null)

  // Measure row height and full grid height; re-measure on resize or slide changes
  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return
    const measure = () => {
      const firstChild = grid.firstElementChild as HTMLElement | null
      if (!firstChild) return
      setRowHeight(firstChild.offsetHeight)
      setGridHeight(grid.scrollHeight)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(grid)
    return () => ro.disconnect()
  }, [slides])

  const hasMultipleRows = rowHeight !== null && gridHeight !== null && gridHeight > rowHeight + 8
  const collapsedHeight = rowHeight !== null ? Math.round(rowHeight * 1.9) : undefined

  // Oldest-first so the newest slide is pinned to the bottom of the grid
  const orderedSlides = useMemo(() => [...slides].reverse(), [slides])

  return (
    <SimpleCard
      title={`Slides${slides && slides.length > 0 ? ` (${slides.length})` : ''}`}
      className="mt-8"
      action={
        <button
          onClick={() => doAddTextGeneratedSlide(placeId)}
          className="px-3 py-1 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
        >
          Add Text Slide
        </button>
      }
    >
      <div>
        {slideLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-mist-500">Loading slides...</p>
          </div>
        ) : slideError ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">Error loading slides: {slideError.message}</p>
          </div>
        ) : slides.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-mist-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="mt-4 text-sm text-gray-500 dark:text-mist-500">No slides available yet</p>
          </div>
        ) : (
          <div className="relative">
            {/* Height-animated clip container — flex-col + justify-end pins latest to bottom */}
            <div
              className="flex flex-col justify-end overflow-hidden transition-[height] duration-500 ease-in-out"
              style={{
                height:
                  hasMultipleRows && !expanded && collapsedHeight !== undefined
                    ? `${collapsedHeight}px`
                    : gridHeight !== null
                      ? `${gridHeight}px`
                      : undefined,
              }}
            >
              <div
                ref={gridRef}
                className="grid gap-4 grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8"
              >
                {orderedSlides.map((slide) => (
                  <SlideCard key={slide.slideId} slide={slide} />
                ))}
              </div>
            </div>

            {/* Top overlay — visible only when collapsed with multiple rows */}
            {hasMultipleRows && (
              <div
                className={`
                  absolute top-0 left-0 right-0 flex items-center justify-center
                  backdrop-blur-sm bg-white/70 dark:bg-mist-900/70
                  transition-[opacity,height] duration-500 ease-in-out overflow-hidden
                  ${expanded ? 'opacity-0 pointer-events-none h-0' : 'opacity-100 h-12'}
                `}
              >
                <button
                  onClick={() => setExpanded(true)}
                  className="px-4 py-1 text-sm font-medium text-blue-700 dark:text-blue-200 rounded-full hover:bg-blue-200/80 dark:hover:bg-blue-700/80 transition-colors"
                >
                  View all slides
                </button>
              </div>
            )}

            {/* Collapse button — shown below grid when expanded */}
            {hasMultipleRows && expanded && (
              <div className="mt-3 flex justify-center">
                <button
                  onClick={() => setExpanded(false)}
                  className="px-4 py-1 text-sm font-medium text-blue-500 dark:text-blue-400 rounded-full hover:bg-blue-100 dark:hover:bg-blue-700 hover:text-blue-700 dark:hover:text-blue-200 transition-colors"
                >
                  Hide old slides
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </SimpleCard>
  )
}

interface SlideCardProps {
  slide: SlideInfo
}

function SlideCard({ slide }: SlideCardProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const transitionName = `slide-${slide.slideId}`

  const openFullscreen = () => {
    if (!('startViewTransition' in document)) {
      setIsFullscreen(true)
      return
    }
    document.startViewTransition(() => {
      flushSync(() => setIsFullscreen(true))
    })
  }

  const closeFullscreen = () => {
    if (!('startViewTransition' in document)) {
      setIsFullscreen(false)
      return
    }
    document.startViewTransition(() => {
      flushSync(() => setIsFullscreen(false))
    })
  }

  useEffect(() => {
    if (!isFullscreen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isFullscreen])

  const handleDownload = async () => {
    if (!slide.imageUrl) return
    try {
      const res = await fetch(slide.imageUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `slide-${slide.slideId}.jpg`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // fallback: open in new tab
      window.open(slide.imageUrl, '_blank')
    }
  }

  const slideImage = slide.imageUrl ? (
    <img
      src={slide.imageUrl}
      alt={`Slide ${slide.slideId}`}
      className="w-full h-full object-cover"
    />
  ) : slide.text || slide.url ? (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-3 bg-gradient-to-br from-gray-700 to-gray-900">
      {slide.text && (
        <span className="text-white text-xs font-medium text-center line-clamp-3 break-words">
          {slide.text}
        </span>
      )}
      {slide.url && (
        <span className="text-blue-300 text-[10px] text-center truncate max-w-full">
          {slide.url}
        </span>
      )}
    </div>
  ) : (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        className="w-12 h-12 text-gray-400 dark:text-mist-700"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  )

  return (
    <>
      {/* Thumbnail card */}
      <div
        className="group relative bg-gray-50 dark:bg-mist-950 rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-mist-800 hover:ring-2 hover:ring-blue-500 dark:hover:ring-blue-400 transition-all cursor-pointer"
        onClick={openFullscreen}
      >
        <div
          className="relative aspect-video bg-gray-100 dark:bg-mist-900"
          style={isFullscreen ? undefined : { viewTransitionName: transitionName }}
        >
          {slideImage}
        </div>

        {/* Slide Info */}
        <div className="px-3 py-1 text-xs font-medium bg-gray-800 dark:bg-mist-950 text-gray-100 dark:text-mist-300">
          <div className="flex items-center justify-between">
            <span className="tabular-nums">
              {formatTimestampToLocalClockTime(slide.timestamp.toString())}
            </span>
            <span className="tabular-nums">
              {formatTimeAgo(slide.timestamp)}
            </span>
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90"
            onClick={closeFullscreen}
          >
            {/* X close button */}
            <button
              className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); closeFullscreen() }}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="text-lg" />
            </button>

            {/* Fullscreen image — shares view-transition-name with the thumbnail */}
            <div
              className="relative w-full max-w-screen-xl max-h-[80vh] aspect-video"
              style={{ viewTransitionName: transitionName }}
              onClick={(e) => e.stopPropagation()}
            >
              {slide.imageUrl ? (
                <img
                  src={slide.imageUrl}
                  alt={`Slide ${slide.slideId}`}
                  className="w-full h-full object-contain"
                />
              ) : slide.text || slide.url ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-8 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg">
                  {slide.text && (
                    <p className="text-white text-2xl font-semibold text-center break-words max-w-prose">
                      {slide.text}
                    </p>
                  )}
                  {slide.url && (
                    <a
                      href={slide.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-300 hover:text-blue-200 text-lg underline break-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {slide.url}
                    </a>
                  )}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <svg className="w-24 h-24 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Bottom toolbar */}
            <div
              className="mt-4 flex items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-white/60 text-sm tabular-nums">
                {formatTimestampToLocalClockTime(slide.timestamp.toString())} &nbsp;·&nbsp; {formatTimeAgo(slide.timestamp)} ago
              </span>
              {slide.imageUrl && (
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/25 text-white text-sm font-medium transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
